import React, { forwardRef } from 'react';
import { ButtonProps } from '../../types/button';
import Typography from '../typography/Typography';
import XIcon from '../icons/XIcon';
import './button.scss';
import Loader from '../loader/Loader';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  label,
  icon,
  onClick,
  type = 'button',
  textColor,
  className,
  formId,
  priority = 'secondary',
  variant = 'info',
  iconPosition = 'left',
  circle = false,
  disabled = false,
  fullWidth = false,
  progress = false,
  infoText = '',
  moreText = '',
  loading = false,
  ...props
}, ref) => {
  const classBase = 'custom-button';

  const classes: string[] = [
    classBase,
    `${classBase}-priority-${priority}`,
    `${classBase}-variant-${variant}`,
    `${className || ''}`,
    `${disabled ? 'disabled' : ''}`,
    `${fullWidth ? 'width-12' : ''}`,
    `${progress ? 'progress' : ''}`,
    `${infoText ? 'info-text' : ''}`,
  ];

  if (circle && icon) classes.push(`${classBase}-circle`);

  return (
    <button
      type={type}
      className={classes.join(' ')}
      data-testid={classBase}
      onClick={() => (onClick ? onClick() : null)}
      disabled={disabled}
      ref={ref}
      form={formId}
      {...props}
    >
      {!!icon && iconPosition === 'left' && (
        <span
          data-testid={`${classBase}-icon`}
          className={`${classBase}-icon left`}
        >
          {icon}
        </span>
      )}

      {(!circle || !icon) && (
        <div className={`${infoText || moreText ? `d-flex ${moreText ? 'flex-row' : 'flex-column'} align-items-center` : ''}`}>
          <Typography className={`button-text ${icon ? iconPosition : ''}`} color={textColor} variant="button">
            {label}
          </Typography>
          {infoText && (
            <Typography className="button-info-text" variant="caption" color={textColor}>
              {infoText}
            </Typography>
          )}
          {moreText && (
            <Typography className="button-more-text" variant="button">
              &nbsp;
              {moreText}
            </Typography>
          )}
        </div>

      )}

      {!!icon && iconPosition === 'right' && (
        <span
          data-testid={`${classBase}-icon`}
          className={`${classBase}-icon right`}
        >
          {icon}
        </span>
      )}
      {progress && <div className="button-progress" />}
      {loading && <div className="button-loader"><Loader spinner /></div>}
      {progress && (
        <div className="progress-cancel-icon">
          <XIcon width={15} color="asphalt6" />
        </div>
      )}
    </button>
  );
});

Button.displayName = 'Button';
